<template>
  <div class="py-4 container-fluid">
    <!-- 返回按钮 -->
    <div class="mb-3">
      <a href="#" @click.prevent="goBack" class="text-sm text-secondary">
        <i class="ni ni-bold-left"></i> 返回商户列表
      </a>
    </div>

    <!-- 加载中 -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">加载中...</span>
      </div>
      <p class="mt-2 text-muted">正在加载商户详情...</p>
    </div>

    <div v-else-if="detail">
      <!-- 商户基本信息卡片 -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center">
            <div class="me-3">
              <div class="avatar avatar-lg bg-gradient-primary rounded-circle">
                <span class="text-white font-weight-bold">{{ merchantDisplayName ? merchantDisplayName.charAt(0) : '?' }}</span>
              </div>
            </div>
            <div class="flex-grow-1">
              <h5 class="mb-0">{{ merchantDisplayName }}</h5>
              <p class="text-sm text-secondary mb-0">
                <span class="me-3">ID: {{ detail.basic_info.id }}</span>
                <span class="me-3" v-if="detail.basic_info.nick_name">昵称: {{ detail.basic_info.nick_name }}</span>
                <soft-badge :color="detail.basic_info.active === 1 ? 'success' : 'danger'">
                  {{ detail.basic_info.active === 1 ? '启用' : '禁用' }}
                </soft-badge>
              </p>
            </div>
          </div>
          <hr class="my-3" />
          <div class="row">
            <div class="col-md-3 col-sm-6 mb-2">
              <small class="text-secondary d-block">地址</small>
              <span class="text-sm">{{ detail.basic_info.address || '-' }}</span>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <small class="text-secondary d-block">联系电话</small>
              <span class="text-sm">{{ detail.basic_info.phone || '-' }}</span>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <small class="text-secondary d-block">手机号</small>
              <span class="text-sm">{{ detail.basic_info.owner_mobile || detail.basic_info.mobile || '-' }}</span>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <small class="text-secondary d-block">汇率</small>
              <span class="text-sm">{{ detail.basic_info.currency_rate || 1 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 概览卡片 -->
      <div class="row mb-3">
        <div class="col-lg-4 col-md-6 mb-2">
          <div class="card">
            <div class="card-body p-3">
              <div class="d-flex align-items-center">
                <div class="icon icon-shape bg-gradient-primary text-white rounded-circle me-3">
                  <i class="ni ni-laptop"></i>
                </div>
                <div>
                  <p class="text-sm text-secondary mb-0">设备总数</p>
                  <h6 class="mb-0">{{ devices.length }}</h6>
                  <small class="text-success" v-if="onlineDeviceCount > 0">在线 {{ onlineDeviceCount }}</small>
                  <small class="text-danger" v-if="offlineDeviceCount > 0"> / 离线 {{ offlineDeviceCount }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4 col-md-6 mb-2">
          <div class="card">
            <div class="card-body p-3">
              <div class="d-flex align-items-center">
                <div class="icon icon-shape bg-gradient-info text-white rounded-circle me-3">
                  <i class="ni ni-shop"></i>
                </div>
                <div>
                  <p class="text-sm text-secondary mb-0">店铺数</p>
                  <h6 class="mb-0">{{ shops.length }}</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4 col-md-6 mb-2">
          <div class="card">
            <div class="card-body p-3">
              <div class="d-flex align-items-center">
                <div class="icon icon-shape bg-gradient-warning text-white rounded-circle me-3">
                  <i class="ni ni-credit-card"></i>
                </div>
                <div>
                  <p class="text-sm text-secondary mb-0">支付通道</p>
                  <h6 class="mb-0">
                    <soft-badge :color="detail.pay_channel.status === '已配置' ? 'success' : (detail.pay_channel.status === '配置中' ? 'warning' : 'danger')">
                      {{ detail.pay_channel.status }}
                    </soft-badge>
                  </h6>
                  <small class="text-secondary" v-if="detail.pay_channel.count > 0">{{ detail.pay_channel.count }} 条记录</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab 导航 -->
      <ul class="nav nav-tabs mb-3" role="tablist">
        <li class="nav-item">
          <a class="nav-link" :class="{ active: activeTab === 'devices' }" href="#" @click.prevent="activeTab = 'devices'">
            设备 ({{ devices.length }})
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{ active: activeTab === 'shops' }" href="#" @click.prevent="activeTab = 'shops'">
            店铺 ({{ shops.length }})
          </a>
        </li>
      </ul>

      <!-- Tab 内容 -->
      <div class="tab-content">
        <!-- 设备 Tab -->
        <div v-show="activeTab === 'devices'">
          <div class="card">
            <div class="card-body">
              <div v-if="devicesLoading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary"></div>
              </div>
              <div v-else-if="devices.length === 0" class="text-center text-muted py-3">
                暂无设备
              </div>
              <div v-else class="table-responsive">
                <table class="table align-items-center">
                  <thead>
                    <tr>
                      <th>设备ID</th>
                      <th>备注名称</th>
                      <th>在线状态</th>
                      <th>固件版本</th>
                      <th>入网时间</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="dev in devices" :key="dev.board_id">
                      <td>{{ dev.board_id }}</td>
                      <td>{{ dev.board_name || '未命名' }}</td>
                      <td>
                        <soft-badge :color="dev.online === 1 ? 'success' : 'secondary'">
                          {{ dev.online === 1 ? '当前在线' : '当前离线' }}
                        </soft-badge>
                      </td>
                      <td>{{ dev.firmware_version || '-' }}</td>
                      <td>{{ formatDate(dev.date) }}</td>
                      <td>
                        <a href="#" @click.prevent="goToDevices(dev.board_id)" class="text-xs text-primary">查看设备</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- 店铺 Tab -->
        <div v-show="activeTab === 'shops'">
          <div class="card">
            <div class="card-body">
              <div v-if="shopsLoading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary"></div>
              </div>
              <div v-else-if="shops.length === 0" class="text-center text-muted py-3">
                暂无店铺
              </div>
              <div v-else class="table-responsive">
                <table class="table align-items-center">
                  <thead>
                    <tr>
                      <th>店铺ID</th>
                      <th>店铺名称</th>
                      <th>位置</th>
                      <th>创建时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="shop in shops" :key="shop.shop_id">
                      <td>{{ shop.shop_id }}</td>
                      <td>{{ shop.shop_name || '-' }}</td>
                      <td>{{ shop.shop_location || '-' }}</td>
                      <td>{{ formatDate(shop.created_date) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-else class="text-center py-5">
      <p class="text-danger">商户信息加载失败</p>
    </div>
  </div>
</template>

<script>
import SoftBadge from "@/components/SoftBadge.vue";
import { getMerchantDetail } from "@/api/merch.js";
import { getboards } from "@/api/board.js";
import { getAllShops } from "@/api/shop.js";
import { getMerchantDisplayName } from "@/utils/merchantCache";

export default {
  name: "MerchantDetail",
  components: { SoftBadge },
  data() {
    return {
      loading: true,
      detail: null,
      activeTab: "devices",
      deliverId: 0,
      // devices
      devicesLoading: false,
      devices: [],
      // shops
      shopsLoading: false,
      shops: [],
      // 商户信息（含进件户名）
      merchantInfo: null,
    };
  },
  computed: {
    merchantDisplayName() {
      if (!this.detail) return '';
      const info = this.detail.basic_info;
      const holder = this.merchantInfo && this.merchantInfo.account_holder ? this.merchantInfo.account_holder : '';
      return holder || info.nick_name || info.name || ('商家' + info.id);
    },
    onlineDeviceCount() {
      return this.devices.filter(d => d.online === 1).length;
    },
    offlineDeviceCount() {
      return this.devices.filter(d => d.online !== 1).length;
    },
  },
  created() {
    this.deliverId = parseInt(this.$route.params.id);
    if (this.deliverId) {
      this.loadDetail();
    }
  },
  watch: {
    activeTab(tab) {
      if (tab === "devices" && this.devices.length === 0) this.loadDevices();
      if (tab === "shops" && this.shops.length === 0) this.loadShops();
    },
  },
  methods: {
    goBack() {
      this.$router.push("/mechants");
    },
    goToDevices(boardId) {
      this.$router.push({ path: "/devices", query: { board_id: boardId, deliver_id: this.deliverId } });
    },
    formatFee(fee) {
      if (!fee) return "0.00";
      return (fee / 100).toFixed(2);
    },
    formatDate(ts) {
      if (!ts) return "-";
      const d = new Date(ts * 1000);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    async loadDetail() {
      this.loading = true;
      try {
        const res = await getMerchantDetail({ deliver_id: this.deliverId });
        if (res.code === 0) {
          this.detail = res.desc;
          await this.loadDevices();
          await this.loadShops();
          this.fetchMerchantInfo();
        } else {
          this.$notify({ type: "danger", message: res.desc || "加载失败" });
        }
      } catch (e) {
        this.$notify({ type: "danger", message: "网络错误" });
      } finally {
        this.loading = false;
      }
    },
    async fetchMerchantInfo() {
      // 使用共享缓存获取商户显示名称
      try {
        const name = await getMerchantDisplayName(this.deliverId, '商家' + this.deliverId);
        this.merchantInfo = this.merchantInfo || {};
        this.merchantInfo.account_holder = name;
      } catch (e) {
        console.error('获取商户信息失败:', e);
      }
    },
    async loadDevices() {
      this.devicesLoading = true;
      try {
        const res = await getboards({ deliver_id: this.deliverId, page: 1, page_size: 100 });
        if (res.code === 0 && res.desc) {
          this.devices = res.desc.devices || [];
        }
      } catch (e) {
        console.error("load devices err", e);
      } finally {
        this.devicesLoading = false;
      }
    },
    async loadShops() {
      this.shopsLoading = true;
      try {
        const res = await getAllShops({ deliver_id: this.deliverId });
        if (res.code === 0 && res.desc) {
          this.shops = Array.isArray(res.desc) ? res.desc.filter(s => s.deliver_id === this.deliverId) : [];
        }
      } catch (e) {
        console.error("load shops err", e);
      } finally {
        this.shopsLoading = false;
      }
    },
  },
};
</script>
